import { LitElement, html, css } from 'lit';

import './neb-icon';
import './neb-skeleton';
import '../../../packages/neb-lit-components/src/components/neb-text';
import {
  CSS_FONT_SIZE_BODY,
  CSS_COLOR_GREY_1,
  CSS_COLOR_GREY_2,
  CSS_COLOR_HIGHLIGHT,
} from '../../styles';

export const ELEMENTS = {
  header: { id: 'header' },
  label: { id: 'label' },
  content: { id: 'content' },
  icon: { id: 'icon' },
  emptyMessage: { id: 'empty-message' },
  slotContent: { id: 'slot-content' },
  skeletonLoading: { id: 'skeleton-loading' },
};

class NebCollapsibleCard extends LitElement {
  static get properties() {
    return {
      label: String,
      showVisibility: { type: Boolean, reflect: true },
      expanded: { type: Boolean, reflect: true },
      emptyMessage: String,
      loading: { type: Boolean, reflect: true },
      onToggleVisibility: { type: Function },
    };
  }

  constructor() {
    super();

    this.initState();
    this.initHandlers();
  }

  initState() {
    this.label = '';
    this.showVisibility = false;
    this.expanded = true;
    this.emptyMessage = '';
    this.defaultMessage = 'No items to display.';
    this.loading = false;

    this.onToggleVisibility = () => {};
  }

  initHandlers() {
    this.__handlers = {
      toggleVisibility: e => {
        e.stopPropagation();
        this.expanded = !this.expanded;
        this.onToggleVisibility?.(this.expanded);
      },
      preventBubbling: e => {
        e.stopPropagation();
      },
    };
  }

  static get styles() {
    return [
      css`
        .container {
          border: 1px solid ${CSS_COLOR_GREY_2};
          border-radius: 5px;
          padding: 10px;
        }

        .content {
          margin-top: 10px;
        }

        ::slotted(li) {
          margin: 0;
          padding: 0;
          line-height: 1.4;
          text-indent: 0;
          list-style-position: outside;
          margin-left: 0.5em;
          padding-left: 0.2em;
        }

        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .label {
          font-size: ${CSS_FONT_SIZE_BODY};
          font-weight: bold;
        }

        .icon {
          width: 16px;
          height: 16px;
          fill: ${CSS_COLOR_HIGHLIGHT};
          cursor: pointer;
        }

        :host(:not([expanded])) .label {
          color: ${CSS_COLOR_GREY_1};
        }

        .empty-message {
          font-style: italic;
          color: ${CSS_COLOR_GREY_1};
        }
      `,
    ];
  }

  __renderVisibilityIcon() {
    return this.showVisibility
      ? html`
          <neb-icon
            id="${ELEMENTS.icon.id}"
            class="icon"
            icon="${this.expanded ? 'neb:visibilityOff' : 'neb:visible'}"
            @click=${this.__handlers.toggleVisibility}
          ></neb-icon>
        `
      : '';
  }

  __renderHeader() {
    return html`
      <div
        id="${ELEMENTS.header.id}"
        class="header"
        @click=${this.__handlers.preventBubbling}
      >
        <span id="${ELEMENTS.label.id}" class="label">${this.label}</span>
        ${this.__renderVisibilityIcon()}
      </div>
    `;
  }

  __renderContent() {
    return this.expanded
      ? html`
          <div
            id="${ELEMENTS.content.id}"
            class="content"
            @click=${this.__handlers.preventBubbling}
          >
            ${this.loading
              ? html`<neb-skeleton
                  id="${ELEMENTS.skeletonLoading.id}"
                ></neb-skeleton>`
              : html`
                  <slot id="${ELEMENTS.slotContent.id}"
                    ><span
                      id="${ELEMENTS.emptyMessage.id}"
                      class="empty-message"
                      >${this.emptyMessage || this.defaultMessage}</span
                    ></slot
                  >
                `}
          </div>
        `
      : '';
  }

  render() {
    return html`
      <div class="container">
        ${this.__renderHeader()} ${this.__renderContent()}
      </div>
    `;
  }
}

window.customElements.define('neb-collapsible-card', NebCollapsibleCard);
